// Temporary per-person dialer grants (Mako 2026-10-27, Norman: "give it to him temporarily").
//
// A listed rep KEEPS their real role in organization_members. Flipping the role would move
// setter pay: resolve_setter_ownership() re-reads it on every outcome write (rolled-back sim
// 2026-10-27: 223 of 242 outcome rows on Norman's leads re-labelled). Instead, this grant only
// lets them claim Lost Leads the way a dialer does: dial credit, never money.
//
// Server mirror, same person + same expiry: the TEMP-DIALER-NORMAN block in claim_lead,
// release_claim_with_reason, extend_claim_hold and get_claim_release_context (the Let go modal)
// (sql/migrations/2026-10-27_norman_temp_dialer_claim.sql). Change both together.
// Revert: delete the entry (frontend) + ship the rollback SQL. Both also lapse on `until`.

export interface TempDialerGrant {
  userId: string;
  name: string;
  /** Grant ends at this instant (ISO, with offset). */
  until: string;
}

export const TEMP_DIALER_GRANTS: TempDialerGrant[] = [
  {
    userId: "64b555f9-a3ae-447a-b82d-337bbfb7c1cb",
    name: "Norman",
    until: "2026-11-27T00:00:00+08:00",
  },
];

export const isTempDialer = (userId: string | undefined | null, now: Date = new Date()): boolean =>
  !!userId &&
  TEMP_DIALER_GRANTS.some(
    (g) => g.userId === userId && now.getTime() < new Date(g.until).getTime()
  );

/** useRBAC.canClaimForDialCredit: a real dialer, or a setter holding a live temp grant.
 *  Pure so the wiring is unit-tested (config/__tests__/tempDialers.test.ts). */
export const canClaimAsDialer = (
  role: string | null | undefined,
  userId: string | undefined | null,
  now: Date = new Date()
): boolean => role === "dialer" || (role === "setter" && isTempDialer(userId, now));

/** Pages a granted setter opens on top of their setter pages (Mako 2026-11-01, Norman:
 *  "actually see his dial analytics"). Sidebar, RouteGuard and mobile nav read this through
 *  roleConfig.canAccessPageForUser; useRBAC.canAccessDialAnalytics reads canSeeDialAnalytics. */
export const TEMP_DIALER_PAGES: string[] = ["/admin/dial-analytics"];

export const canOpenPageAsTempDialer = (
  role: string | null | undefined,
  path: string,
  userId: string | undefined | null,
  now: Date = new Date()
): boolean =>
  role === "setter" &&
  isTempDialer(userId, now) &&
  TEMP_DIALER_PAGES.some((p) => path === p || path.startsWith(`${p}/`));

/** useRBAC.canAccessDialAnalytics: exec, closer, dialer, or a setter holding a live grant. */
export const canSeeDialAnalytics = (
  role: string | null | undefined,
  userId: string | undefined | null,
  now: Date = new Date()
): boolean =>
  ["exec", "closer", "dialer"].includes(role || "") ||
  canOpenPageAsTempDialer(role, "/admin/dial-analytics", userId, now);
